import { Recipe } from 'application/Shared/types/recipe';

import {
    SaasOpsActionItemStatus,
    SaasOpsActionItemsState,
    WorkatoIdpProviders
} from './types';

/**
 * Sorts and builds a SaaS Ops action items state from a list of recipes.
 *
 * @param recipes - Array of recipes to process
 * @returns SaasOpsActionItemsState - Sorted and filtered array of action items with status
 *
 * The function:
 * 1. Filters out recipes with no app or with no workatoProvider
 * 2. Sorts recipes by:
 *    - IDP providers first (from WorkatoIdpProviders enum)
 *    - Alphabetically by app name within each group
 * 3. Maps to SaasOpsActionItemsState format with Pending status
 */
export const sortAndBuildSaasOpsActionItemsState = (
    recipes: Recipe[],
    applicationIds: string[] = []
): SaasOpsActionItemsState => {
    let filteredRecipes = recipes
        // Filter out any apps with no workatoProvider set, just in case.
        .filter((recipe) => !!recipe?.application?.workatoProvider);

    if (applicationIds.length) {
        filteredRecipes = filteredRecipes.filter((recipe) =>
            applicationIds.includes(recipe.applicationId!)
        );
    }

    return filteredRecipes
        .sort((recipeA: Recipe, recipeB: Recipe) => {
            // The filter above ensures that we only have recipes with an application AND a workatoProvider.
            const applicationA = recipeA.application!;
            const workatoProviderA = applicationA.workatoProvider!;

            const applicationB = recipeB.application!;
            const workatoProviderB = applicationB.workatoProvider!;

            const isIdpProviderA = (
                Object.values(WorkatoIdpProviders) as string[]
            ).includes(workatoProviderA);

            const isIdpProviderB = (
                Object.values(WorkatoIdpProviders) as string[]
            ).includes(workatoProviderB);

            const aName = applicationA.name!;
            const bName = applicationB.name!;

            if (isIdpProviderA && isIdpProviderB && aName && bName) {
                return aName.localeCompare(bName);
            }

            if (isIdpProviderA) {
                return -1;
            }
            if (isIdpProviderB) {
                return 1;
            }

            return aName.localeCompare(bName);
        })
        .map((recipe: Recipe) => {
            const application = recipe.application!;
            const workatoProvider = application.workatoProvider!;

            return {
                applicationId: recipe.applicationId!,
                recipe,
                status: SaasOpsActionItemStatus.Pending,
                workatoProvider
            };
        });
};
